// TaminLoginModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Modal from "../general/modal";
import {
  isShowModalDataSelector,
  modalDataSelector,
  modalNameSelector,
} from "../../../store/selector/general";
import { general } from "../../../store/action";
import Icon from "../general/icon";
import {
  useGetTaminAuth,
  useVerifyTaminAuth,
} from "../../../hook/request/providerTamin";
import BoxLoader from "../general/boxLoader";
import { localStorageGet, localStorageSet } from "../../../utils/localStorage";
import Noti from "../general/noti";

type Props = {
  modalData: any;
  modalName: string;
  isShowModalData: boolean;
  handleHideModal: any;
  handleResetModal: any;
};

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 700;

// helper: build center position
const buildPopupFeatures = () => {
  const left = Math.max(
    0,
    Math.floor(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2),
  );
  const top = Math.max(
    0,
    Math.floor(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2),
  );
  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`;
};

const TaminLoginModal = ({
  modalData,
  modalName,
  isShowModalData,
  handleHideModal,
  handleResetModal,
}: Props) => {
  const [showState, setShowState] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const [loginUrl, setLoginUrl] = useState<string | null>(null);

  // fetch Tamin auth url (same as before)
  const getProviderTaminAuthRequest = useGetTaminAuth(
    10,
    modalName === "taminLoginModal",
  );
  const verifyTaminAuthQuery = useVerifyTaminAuth();

  useEffect(() => {
    if (isShowModalData && modalName === "taminLoginModal") {
      setShowState(true);
      handleResetModal();
    }
  }, [isShowModalData, modalName]);

  useEffect(() => {
    const res = getProviderTaminAuthRequest.data?.data;
    if (!res) return;

    // BUSINESS ERROR (status !== 200)
    if (res.status !== 200) {
      Noti("error", res.msg || "خطا در دریافت لینک ورود تامین اجتماعی");
      closePopupAndModal(false);
      return;
    }

    // SUCCESS → show login button, keep modal open
    if (res.result) {
      setLoginUrl(res.result);
    }
  }, [getProviderTaminAuthRequest.data]);

  // message listener: receive from redirect page
  useEffect(() => {
    const handler = (ev: MessageEvent) => {
      try {
        // ensure message from our domain
        // const allowedOrigin = window.location.origin;
        // if (ev.origin !== allowedOrigin) return;
        const allowedOrigins = [
          window.location.origin,
          "https://sai-panel.sairantelemetry.ir",
        ];

        if (!allowedOrigins.includes(ev.origin)) return;
        const payload = ev.data;
        if (!payload || payload.type !== "tamin_auth") return;

        console.log("TAMIN_09", payload);
        verifyTaminAuthQuery.mutate(payload.code);
        // store to localStorage (or call verify flow)
        localStorageSet("TAMIN_REDIRECT_PAYLOAD", payload);
        // optionally call backend to verify here by refetching your hook if needed
        Noti("success", "کد تأیید دریافت شد. در حال پردازش...");
        // closePopupAndModal();
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (!verifyTaminAuthQuery.isSuccess) return;

    const result = verifyTaminAuthQuery.data?.data?.result;
    if (!result || typeof result !== "string") {
      Noti("error", "پاسخ نامعتبر از تامین اجتماعی");
      closePopupAndModal(false);
      return;
    }

    try {
      const url = new URL(result);
      const ok = url.searchParams.get("ok");
      const message = url.searchParams.get("message");

      if (ok === "false") {
        Noti(
          "error",
          message ? decodeURIComponent(message) : "احراز هویت ناموفق بود",
          undefined,
          5000,
        );
        closePopupAndModal(false);
        return;
      }

      if (ok === "true") {
        Noti("success", "احراز هویت تامین اجتماعی با موفقیت انجام شد.");
        closePopupAndModal(true);
        return;
      }

      // unexpected case
      Noti("error", "پاسخ نامعتبر از تامین اجتماعی");
      closePopupAndModal(false);
    } catch {
      Noti("error", "خطا در پردازش پاسخ تامین اجتماعی");
      closePopupAndModal(false);
    }
  }, [verifyTaminAuthQuery.isSuccess, verifyTaminAuthQuery.data]);

  const closePopupAndModal = (isSuccess: boolean = false) => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }

    if (isSuccess) {
      // let success toast be visible
      setTimeout(() => {
        setShowState(false);
        setLoginUrl(null);
        handleHideModal();
      }, 1200);
    } else {
      // close immediately on error
      setShowState(false);
      setLoginUrl(null);
      handleHideModal();
    }
  };

  const openPopup = () => {
    if (!loginUrl) return;
    popupRef.current = window.open(
      loginUrl,
      "tamin_login",
      buildPopupFeatures(),
    );
    if (!popupRef.current) {
      Noti("error", "Popup blocked. Please allow popups for this site.");
      return;
    }
    popupRef.current.focus();

    // optional: fallback polling to detect close
    const checkInterval = setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        clearInterval(checkInterval);
        // read from localStorage, if redirect finished store token and close modal
        const payload = localStorage.getItem("TAMIN_REDIRECT_PAYLOAD");
        if (payload) {
          // we already handled via postMessage normally
          closePopupAndModal();
        }
      }
    }, 500);
  };

  return (
    <>
      <Modal
        setShow={closePopupAndModal}
        showState={showState}
        noCloseBtn
        className="w-1000 mw-100"
        bodyClassName="px-3 px-lg-5 pos-rel"
      >
        <div className="form-box w-1000 mw-100 mx-auto py-3">
          <div className="d-flex mb-4 fs-3 px-2">
            <div className="px-2 flex-fill align-content-center">
              <h4 className="d-flex align-items-center title-dot font-16 font-weight-bold">
                ورود به سامانه تامین اجتماعی
              </h4>
            </div>
            <span
              className="text-red-hover align-content-center mt-2"
              onClick={() => closePopupAndModal(false)}
            >
              <Icon name="close-square" />
            </span>
          </div>

          {loginUrl ? (
            <>
              <div className="px-3 pb-3">
                <button className="btn btn-primary" onClick={openPopup}>
                  ورود به حساب کاربری تامین اجتماعی
                </button>
              </div>
              <div className="px-3">
                <small className="text-muted">
                  لطفا در پنجره باز شده مشخصات حساب کاربری تامین اجتماعی خود را
                  وارد کنید.
                  <br></br>
                  در صورت بسته شدن صفحه، دوباره تلاش کنید.
                </small>
              </div>
            </>
          ) : (
            <BoxLoader type="cover" />
          )}
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  modalData: modalDataSelector(state),
  modalName: modalNameSelector(state),
  isShowModalData: isShowModalDataSelector(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  handleHideModal: () => dispatch(general.handleHideModal()),
  handleResetModal: () => dispatch(general.handleResetModal()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaminLoginModal);

// import React, { useEffect, useState } from "react";
// import { connect } from "react-redux";
// import Modal from "../general/modal";
// import {
//   isHideModalDataSelector,
//   isShowModalDataSelector,
//   modalDataSelector,
//   modalNameSelector,
// } from "../../../store/selector/general";
// import { general } from "../../../store/action";
// import Icon from "../general/icon";
// import {
//   useGetTaminAuth,
//   useVerifyTaminAuth,
// } from "../../../hook/request/providerTamin";
// import BoxLoader from "../general/boxLoader";
// import { useMySearchParams } from "../../../hook/useMySearchParams";
// import { localStorageSet } from "../../../utils/localStorage";
// import Noti from "../general/noti";

// type TaminLoginModalProps = {
//   modalData: any;
//   modalName: string;
//   isShowModalData: boolean;
//   handleHideModal: any;
//   handleResetModal: any;
// };

// type ModalInfoProps = {
//   checkForTaminAuthorization?: () => void;
// };

// // ورود به سامانه تامین اجتماعی
// // اول وارد لاگین تامین میشیم، با وارد کردن اطلاعات به سایت خودمون ریدایرکت می‌شیم و یک توکن به شکل url از تامین میگیریم.
// // توکن رو برای ریکوئست وریفای میفرستیم و از طریق اون توکن اصلی رو از تامین میگیریم و سیو میکنیمش.
// const TaminLoginModal = ({
//   modalData,
//   modalName,
//   isShowModalData,
//   handleHideModal,
//   handleResetModal,
// }: TaminLoginModalProps) => {
//   const { getQueryParams } = useMySearchParams();
//   const [showState, setShowState] = useState(false);
//   const [modalInfo, setModalInfo] = useState<ModalInfoProps>({
//     checkForTaminAuthorization: undefined,
//   });
//   const [currentUrl, setCurrentUrl] = useState("");
//   const [isRedirected, setIsRedirected] = useState(false);
//   const initialRedirectData = {
//     authorizationCode: "",
//     verifierCode: "",
//     expiresIn: "",
//   };
//   const [redirectData, setRedirectData] = useState(initialRedirectData);

//   const getProviderTaminAuthRequest = useGetTaminAuth(
//     10, // web->10, application->20 @AMIR
//     modalName === "taminLoginModal"
//   );
//   const verifyTaminAuthRequest = useVerifyTaminAuth(
//     getQueryParams(redirectData),
//     false
//   );

//   useEffect(() => {
//     if (isShowModalData && modalName === "taminLoginModal") {
//       setShowState(true);
//       setModalInfo(modalData);
//       // setFieldValue({...fieldValue, nationalCode: modalData?.nationalCode})
//       handleResetModal();
//       var data = {
//         id: modalData?.id,
//       };
//     }
//   }, [modalData, modalName]);

//   useEffect(() => {
//     if (getProviderTaminAuthRequest?.data?.data?.result) {
//       setCurrentUrl(getProviderTaminAuthRequest?.data?.data?.result);
//       console.log("TAMIN_05", getProviderTaminAuthRequest?.data?.data?.result);
//     }
//   }, [getProviderTaminAuthRequest?.data]);

//   useEffect(() => {
//     if (
//       redirectData?.authorizationCode !== "" &&
//       redirectData?.verifierCode !== ""
//     ) {
//       verifyTaminAuthRequest?.refetch();
//     }
//   }, [redirectData]);

//   // بعد از دریافت توکن داخل استورج سیوش میکنیم.
//   useEffect(() => {
//     if (verifyTaminAuthRequest?.data?.data?.result) {
//       // console.log('verify', verifyTaminAuthRequest?.data?.data?.result)
//       localStorageSet("TAMIN_ACCESS_TOKEN", {
//         ...verifyTaminAuthRequest?.data?.data?.result,
//         date_time: Date.now(),
//       });

//       // باید به پنل صاایران ریدایرکت شده باشیم حتما
//       const devServerUrl = window.location.origin;
//       const envUrl = process.env.REACT_APP_TAMIN_REDIRECT_ENV;
//       if (
//         envUrl &&
//         devServerUrl &&
//         currentUrl.includes(`${devServerUrl}${envUrl}`)
//       ) {
//         Noti("success", "ورود شما به سامانه تأمین اجتماعی با موفقیت انجام شد!");
//         handleSetShowModal();
//         modalInfo?.checkForTaminAuthorization &&
//           modalInfo.checkForTaminAuthorization();
//       }
//     }
//   }, [verifyTaminAuthRequest?.data]);

//   const handleSetShowModal = () => {
//     setShowState(false);
//     setCurrentUrl("");
//     setRedirectData(initialRedirectData);
//     handleHideModal();
//   };

//   // صفحه داخل آیفریم که عوض بشه در صورتی که وارد پنل خودمون شده باشیم میتونیم url رو ببینیم و بفهمیم که موفقیت آمیز بوده.
//   const handleIframeLoad = () => {
//     const iframe = document.getElementById(
//       "tamin_login_iframe"
//     ) as HTMLIFrameElement;
//     if (iframe && iframe.contentWindow) {
//       try {
//         const iframeLocation = iframe.contentWindow.location.href;
//         const devServerUrl = window.location.origin;
//         const envUrl = process.env.REACT_APP_TAMIN_REDIRECT_ENV;
//         if (
//           envUrl &&
//           devServerUrl &&
//           iframeLocation.includes(`${devServerUrl}${envUrl}`)
//         ) {
//           setIsRedirected(true);
//           const parsedData = parseRedirectedURL(iframeLocation);
//           setRedirectData(parsedData);
//           setCurrentUrl(iframeLocation); // Update the URL if needed

//           // Optionally, you can perform additional actions here
//         }
//       } catch (e) {}
//     }
//   };

//   // دیتا داخل پاسخ تامین رو پارس'ا' میکنیم
//   const parseRedirectedURL = (url: string) => {
//     const parsedURL = new URL(url);
//     const pathSegments = parsedURL.pathname
//       .split("/")
//       .filter((segment) => segment !== "");
//     const authorizationCode = pathSegments.length > 1 ? pathSegments[1] : "";

//     const params = new URLSearchParams(parsedURL.search);
//     const verifierCode = params.get("code") || "";
//     const expiresIn = params.get("expires_in") || "";

//     return { authorizationCode, verifierCode, expiresIn };
//   };

//   return (
//     <Modal
//       setShow={handleSetShowModal}
//       showState={showState}
//       noCloseBtn={true}
//       className="w-1000 mw-100"
//       bodyClassName="px-3 px-lg-5 pos-rel"
//     >
//       {/*{patientAdmissionRequest?.isPending && <BoxLoader type="cover"/>}*/}
//       <div className="form-box w-1000 mw-100 mx-auto py-3">
//         <div className="d-flex mb-4 fs-3 px-2">
//           <div className=" px-2 flex-fill align-content-center">
//             <h4 className="d-flex align-items-center title-dot font-16 font-weight-bold">
//               ورود به سامانه تامین اجتماعی
//             </h4>
//           </div>
//           <span
//             className="text-red-hover align-content-center mt-2"
//             onClick={() => handleSetShowModal()}
//           >
//             <Icon name="close-square" />
//           </span>
//         </div>
//         {currentUrl ? (
//           <iframe
//             id={"tamin_login_iframe"}
//             src={currentUrl}
//             title="Website"
//             width="100%"
//             height="400px"
//             style={{ border: "none" }}
//             onLoad={handleIframeLoad} // Attach the onLoad handler
//           />
//         ) : (
//           <BoxLoader type="cover" />
//         )}
//       </div>
//     </Modal>
//   );
// };

// const mapStateToProps = (state: any) => ({
//   modalData: modalDataSelector(state),
//   modalName: modalNameSelector(state),
//   isShowModalData: isShowModalDataSelector(state),
//   isHideModalData: isHideModalDataSelector(state),
// });

// const mapDispatchToProps = (dispatch: any) => ({
//   handleHideModal: () => dispatch(general.handleHideModal()),
//   handleResetModal: () => dispatch(general.handleResetModal()),
// });

// export default connect(mapStateToProps, mapDispatchToProps)(TaminLoginModal);
